<!--
  - Copyright 2020 AlexaCRM
  -
  - Permission is hereby granted, free of charge, to any person obtaining a copy of this software and
  - associated documentation files (the "Software"), to deal in the Software without restriction, including
  - without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  - copies of the Software, and to permit persons to whom the Software is furnished to do so,
  - subject to the following conditions:
  -
  - The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
  -
  - THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
  - INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
  - PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS
  - BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
  - TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE
  - OR OTHER DEALINGS IN THE SOFTWARE.
  -->

<template>
  <div class="icds-form">
    <template v-if="this.settings.noajax===false && (keepForm || !submitted || !result.isSuccessful)">
      <div @submit.prevent="submit" ref="formContainer">
        <keep-alive>
          <component ref="template" :is="customFormTemplate" :form="form"
                     @loaded="onRecaptchaLoaded" @solved="onRecaptchaSolved"
                     @solved-invisible="onRecaptchaInvisibleSolved" @failed="onRecaptchaFailed"/>
        </keep-alive>
      </div>
      <Spinner v-if="submitting"/>
      <template v-else-if="submitted">
        <div class="alert alert-danger" v-if="!result.isSuccessful">
          {{failureMessage}}
          <div v-html="result.errorMessage"></div>
        </div>
        <div class="alert alert-success" v-else>
          {{successMessage}}
        </div>
      </template>
    </template>
    <template v-else-if="this.settings.noajax===true">
      <div ref="formContainer">
        <keep-alive>
          <component ref="template" :is="customFormTemplate" :form="form"
                     @loaded="onRecaptchaLoaded" @solved="onRecaptchaSolved"
                     @solved-invisible="onRecaptchaInvisibleSolved" @failed="onRecaptchaFailed"/>
        </keep-alive>

      </div>
    </template>
    <template v-else>
      <div class="alert alert-success">
        {{successMessage}}
      </div>
    </template>
  </div>
</template>

<script>
  import Api from '@/util/api'
  import map from 'lodash/map'
  import trim from 'lodash/trim'
  import Spinner from '@/components/Spinner'
  import VueRecaptcha from '@/components/VueRecaptcha'
  import {TYPE_V2_INVISIBLE, TYPE_V3} from '@/store/settings/recaptcha'
  import i18n from '@/mixins/i18n'

  const authRefs = window.icdsAuthRefs

  const innerComponent = {
    props: ['form'],
    components: {VueRecaptcha},
    mixins: [i18n],
    methods: {
      /*
       * reCAPTCHA proxy handlers.
       */
      onRecaptchaLoaded: function() {
        this.$emit('loaded')
      },
      onRecaptchaSolved: function(token) {
        this.$emit('solved', token)
      },
      onRecaptchaInvisibleSolved: function(token) {
        this.$emit('solved-invisible', token)
      },
      onRecaptchaFailed: function() {
        this.$emit('failed')
      }
    }
  }

  function convertToBase64Async(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onloadend = () => {
        resolve(reader.result.replace(/.+base64,/, ''))
      }
      reader.onerror = error => {
        reject(error)
      }
      reader.readAsDataURL(file)
    })
  }

  export default {
    name: 'CustomForm',
    components: {Spinner},
    props: ['form'],
    mixins: [i18n],
    data() {
      return {
        values: {},
        filePromises: [],
        submitted: false,
        submitting: false,
        result: null,

        isRecaptchaSolved: false,
        recaptchaToken: null
      }
    },

    mounted: function() {
      if(this.form.isRecaptchaEnabled) {
        this.submitting = true
      }

      this.$refs.formContainer.dispatchEvent(new CustomEvent('icds.forms.created', {
        bubbles: true,
        detail: {
          form: this.form.id,
          fields: this.values
        }
      }))

      console.log('INFO. ICDS custom from created.');
    },

    computed: {
      settings() {
        return this.form.formSettings
      },

      customFormTemplate: function() {
        return {...innerComponent, template: `#icds-tpl-custom-form-code-${this.form.id}`}
      },

      hasRedirect: function() {
        return this.settings.redirect !== ''
      },

      successMessage: function() {
        if(this.settings.messages && this.settings.messages.success) {
          return this.settings.messages.success
        }

        if(this.settings.message) {
          return this.settings.message
        }

        return this.__('Thanks for submitting the form.', 'integration-cds')
      },

      failureMessage: function() {
        if(this.settings.messages && this.settings.messages.failure) {
          return this.settings.messages.failure
        }

        return this.__('Failed to submit the form.', 'integration-cds')
      },

      keepForm: function() {
        return this.settings.keep === true || this.settings.keep === 'true'
      },

      clearValues: function() {
        if(this.settings.mode !== 'create') {
          return false
        }

        return this.settings.keepData === false || this.settings.keepData === 'false'
      },

      authRef() {
        return authRefs[this.form.id]
      }
    },
    methods: {
      hydrateValues() {
        this.$el.querySelectorAll('input, select, textarea').forEach(el => {
          const fieldName = el.name

          if(el.type === 'file') {
            if(el.files.length === 0) {
              return // continue
            }

            const file = el.files[0]

            this.filePromises.push(new Promise((resolve, reject) => {
              convertToBase64Async(el.files[0]).then(contents => {
                this.values[fieldName] = contents

                // Corner case for the annotation table.
                if(fieldName === 'documentbody') {
                  this.values.filename = file.name
                  this.values.mimetype = file.type
                } else {
                  this.values[`${fieldName}_name`] = file.name
                }
                resolve()
              }).catch(e => {
                reject(e)
              })
            }))

            return // continue
          }

          let val = el.value

          if(el.type === 'checkbox') {
            val = el.checked
          } else if(el.type === 'radio'){
            if (!el.checked){
              return // continue
            }

            val = parseInt(val, 10)
          } else if (el.type === 'select-multiple') {
            val = map(el.selectedOptions, o => o.value).join(',')
          } else if(trim(val) === '') {
            val = null
          }

          this.$set(this.values, fieldName, val)
          this.values[fieldName] = val
        })
      },

      clearControls() {
        this.values = {}
        this.$el.querySelectorAll('input:not([type="hidden"]), textarea').forEach(el => el.value = '')
        this.$el.querySelectorAll('select').forEach(el => el.selectedIndex = -1)
      },

      submit: function() {
        const headers = {}

        this.submitting = true
        this.hydrateValues()

        headers['X-ICDS-FormAuthRef'] = this.authRef

        if (this.settings.mode === 'update') {
          headers['X-ICDS-RecordId'] = JSON.stringify(this.settings.record)
        }

        if (this.form.isRecaptchaEnabled) {
          /*
           * Invisible reCAPTCHA (v2, v3) might not be solved by this moment.
           * Make it appear like we're still submitting the form, but interrupt for now
           * and retrieve the real token. The callback will then resubmit the form.
           *
           * (See onRecaptchaInvisibleSolved.)
           */
          if ((this.form.recaptchaSettings.type === TYPE_V3 || this.form.recaptchaSettings.type === TYPE_V2_INVISIBLE)
            && !this.isRecaptchaSolved) {
            this.$refs.template.$refs.recaptcha.solve()
            return
          }

          headers['X-ICDS-Recaptcha'] = this.recaptchaToken
        }

        const cancelled = !this.$refs.formContainer.dispatchEvent(new CustomEvent('icds.forms.submitting', {
          bubbles: true,
          cancelable: true,
          detail: {
            form: this.form.id,
            fields: this.values
          }
        }))

        if (cancelled) {
          return;
        }

        this.submitted = false
        this.submitting = true

        Promise.all(this.filePromises).then(() => {
          return Api().post('custom_form_submission', this.values, {headers})
        }).then(r => {
          const result = r.data
          if(result.isSuccessful === true && this.hasRedirect) {
            let target = this.settings.redirect
            if(result.reference && result.reference.Id) {
              target = target.replace('%s', result.reference.Id)
            }

            window.location.href = target
            return
          }

          this.result = result

          if(this.clearValues && result.isSuccessful === true) {
            this.clearControls()
          }

          if(this.form.isRecaptchaEnabled) {
            this.isRecaptchaSolved = false
            this.$refs.template.$refs.recaptcha.reload()
          }

          if (result.isSuccessful){
            this.$refs.formContainer.dispatchEvent(new CustomEvent('icds.forms.submitted', {
              bubbles: true,
              detail: {
                form: this.form.id,
                fields: this.values
              }
            }))
          }else{
            this.$refs.formContainer.dispatchEvent(new CustomEvent('icds.forms.error', {
              bubbles: true,
              detail: {
                form: this.form.id,
                fields: this.values,
                error: result.errorMessage,
              }
            }))
          }

          if (r.headers['x-icds-duration']){
            console.info( 'ICDS Debug: Execution time is ', r.headers['x-icds-duration'] )
          }
        }).catch(e => {
          this.result = {
            isSuccessful: false,
            errorMessage: e.message || e
          }

          this.$refs.formContainer.dispatchEvent(new CustomEvent('icds.forms.error', {
            bubbles: true,
            detail: {
              form: this.form.id,
              fields: this.values,
              error: e.message || e,
            }
          }))

        }).finally(() => {
          this.submitting = false
          this.submitted = true
        })
      },

      /*
       * reCAPTCHA handlers.
       */
      onRecaptchaLoaded() {
        this.submitting = false
      },
      onRecaptchaSolved(token) {
        this.isRecaptchaSolved = true
        this.recaptchaToken = token
      },
      onRecaptchaInvisibleSolved(token) {
        this.onRecaptchaSolved(token)

        /*
         * Invisible reCAPTCHA (v2, v3) might not be solved by the moment the form is submitted.
         * This method handles the reCAPTCHA component `solved-invisible` event.
         * The submission has been started in the `submit()` method and now it is run again,
         * with reCAPTCHA solved.
         */
        this.submit()
      },
      onRecaptchaFailed() {
        this.isRecaptchaSolved = false
        this.recaptchaToken = null
      }
    }
  }
</script>
